// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import { Row, Col, Spin } from 'antd'
import { useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type FieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { FundInvestmentSummaryTable } from '../../FundInvestmentSummaryTable.tsx'
import { AgentSignalsSection } from '../../../../internal_agents/components/AgentSignalsSection.tsx'
import { fundStatusTag } from '../../../../global_configs/fundStatusDisplay.tsx'

interface DashboardTabProps {
  detail: Record<string, unknown> | null
  detailLoading: boolean
  fundId: string | null
}

const txt = (v: unknown): string => (v == null || v === '' ? '–' : String(v))

const readOnlyText = (key: string, label: string, value: unknown): FieldConfig =>
  ({
    key,
    label,
    type: FieldTypeEnum.TEXT,
    value: txt(value),
    editable: false
  } as FieldConfig)

export function DashboardTab({ detail, detailLoading, fundId }: DashboardTabProps): React.ReactElement {
  const d = detail ?? {}
  const loading = detailLoading

  // Fund lifecycle status (fund status, 2026-09-04) — two read-only Tags in the
  // Summary header, the documented slot for status furniture. Editing lives on
  // the Terms tab.
  //
  // The detail RPC carries the status NAMES; the behaviour flags that pick the
  // Tag colour come from globalConfigs. Resolving them here rather than
  // widening the RPC keeps the colour rule in one place — and the fund row is
  // already in the store for every fund the user can open.
  const fundStatuses = useSelector((state: any) => state.globalConfigs?.fund_statuses ?? [])
  const globalFunds = useSelector((state: any) => state.globalConfigs?.funds ?? [])
  const statusTags = useMemo(() => {
    const fund = globalFunds.find((f: any) => f?.id === fundId)
    const rowFor = (statusId: unknown) => fundStatuses.find((s: any) => s?.id === statusId) ?? null
    const fr = rowFor(fund?.fundraising_status_id)
    const inv = rowFor(fund?.investment_status_id)
    // NAME from globalConfigs too, not from the detail RPC. The Terms tab keeps
    // the store's fund row in step on save, but nothing refetches this tab's
    // detail — reading the name from the RPC left the Tag showing the
    // pre-edit status until a full reload. The RPC's name is the fallback for
    // a fund somehow absent from the store.
    const tags = [
      fundStatusTag(fr?.name ?? (d.funds__fundraising_status__name as string | null), fr),
      fundStatusTag(inv?.name ?? (d.funds__investment_status__name as string | null), inv)
    ].filter(Boolean)
    return tags.length > 0 ? <>{tags}</> : null
  }, [globalFunds, fundStatuses, fundId, d.funds__fundraising_status__name, d.funds__investment_status__name])

  const identityFields: FieldConfig[] = useMemo(
    () => [
      readOnlyText('funds__name', 'Name', d.funds__name),
      readOnlyText('funds__legal_name', 'Legal Name', d.funds__legal_name),
      readOnlyText('funds__initial_closing_date', 'Initial Close Date', d.funds__initial_closing_date_fmt),
      readOnlyText('funds__final_closing_date', 'Final Close Date', d.funds__final_closing_date_fmt),
      readOnlyText('funds__investment_period_end_date', 'Investment Period End', d.funds__investment_period_end_date_fmt)
    ],
    [d.funds__name, d.funds__legal_name, d.funds__initial_closing_date_fmt, d.funds__final_closing_date_fmt, d.funds__investment_period_end_date_fmt]
  )

  const capitalFields: FieldConfig[] = useMemo(
    () => [
      readOnlyText('funds__committed_capital', 'Committed Capital', d.funds__committed_capital_fmt),
      readOnlyText('funds__closed_capital', 'Closed Capital', d.funds__closed_capital_fmt),
      readOnlyText('funds__called_capital', 'Called Capital', d.funds__called_capital_fmt)
    ],
    [d.funds__committed_capital_fmt, d.funds__closed_capital_fmt, d.funds__called_capital_fmt]
  )

  const performanceFields: FieldConfig[] = useMemo(
    () => [
      readOnlyText('cost_incurred', 'Invested', d.cost_incurred_fmt),
      readOnlyText('proceeds', 'Realized Value', d.proceeds_fmt),
      readOnlyText('fmv', 'Unrealized Value', d.fmv_fmt),
      readOnlyText('moic', 'MOIC', d.moic_fmt),
      readOnlyText('irr', 'Gross IRR', d.irr_fmt),
      readOnlyText('realized_invested', 'Realized / Invested', d.realized_invested_fmt),
      readOnlyText('perc_exited', '% Exited (by cost)', d.perc_exited_fmt),
      readOnlyText('funds__avg_entry_valuation', 'Avg. Entry Valuation', d.funds__avg_entry_valuation_fmt)
    ],
    [d.cost_incurred_fmt, d.proceeds_fmt, d.fmv_fmt, d.moic_fmt, d.irr_fmt, d.realized_invested_fmt, d.perc_exited_fmt, d.funds__avg_entry_valuation_fmt]
  )

  const activityFields: FieldConfig[] = useMemo(
    () => [
      readOnlyText('investments', 'No. Investments', d.investments_fmt),
      readOnlyText('companies', 'No. Companies', d.companies_fmt),
      readOnlyText('no_exited', 'No. Exited', d.no_exited_fmt),
      readOnlyText('no_active', 'No. Active', d.no_active_fmt),
      readOnlyText('avg_check', 'Avg. Check Size', d.avg_check_fmt)
    ],
    [d.investments_fmt, d.companies_fmt, d.no_exited_fmt, d.no_active_fmt, d.avg_check_fmt]
  )

  if (loading && !detail) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div className='funds-dashboard flex flex-col h-full p-3 gap-3 overflow-hidden'>
      {/* Inline-edit value boxes match the muted label colour. Field labels
          themselves now default to rgba(0,0,0,0.45) in InputManager, so the
          old label override here is no longer needed. */}
      <style>{`
        .funds-dashboard .inline-edit-label {
          color: rgba(0, 0, 0, 0.45) !important;
          font-weight: 400 !important;
        }
      `}</style>

      <FormSection title='Summary' titleColor='blue' scrollable={false} headerActions={statusTags}>
        <div className='p-3'>
          <Row gutter={24}>
            <Col xs={24} md={6}>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={identityFields}
                fieldWrapping={true}
              />
            </Col>
            <Col xs={24} md={6}>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={performanceFields}
                fieldWrapping={true}
              />
            </Col>
            <Col xs={24} md={6}>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={activityFields}
                fieldWrapping={true}
              />
            </Col>
            <Col xs={24} md={6}>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={capitalFields}
                fieldWrapping={true}
              />
            </Col>
          </Row>
        </div>
      </FormSection>

      {/* The fund trackers' open signals (LP agent programme Phase 5:
          highlights_ready when a period highlights draft awaits review).
          openOnly, capped, hidden when quiet — the composition every agent
          surface shares. */}
      {fundId && (
        <AgentSignalsSection
          contextTable='funds'
          contextId={fundId}
          openOnly
          maxVisible={3}
          hideWhenEmpty
          collapseKey='fund_dashboard_signals'
        />
      )}

      <div className='flex-1 min-h-0 flex flex-col'>
        <FundInvestmentSummaryTable
          fundId={fundId}
          fundName={typeof d.funds__name === 'string' ? d.funds__name : null}
        />
      </div>
    </div>
  )
}

export default DashboardTab
